import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useState } from 'react';
import { Prompt, useHistory } from 'react-router';
import { EditorMode } from '../../../../modules/editor/models/EditorModes';
import { useLoader } from '../../../helpers/useLoader';
import { Home } from '../../../routes/config/Home';
import { IAnnotationsStore } from '../../annotation/annotations.store';
import { IAnnotationUiStore } from '../../annotation/annotationUi.store';
import { QuitAnnotationsModal } from '../../annotation/components/QuitAnnotationsModal';
import { SaveDraftAnnotationsModal } from '../../annotation/components/SaveDraftAnnotationsModal';
import { IUndoRedoHistory } from '../../annotation/undoRedoHistory.service';
import { IFreeAccessService } from '../freeAccess.service';
import { AnnotateStage, FreeAccessMode, IFreeAccessStore } from '../freeAccess.store';

interface IProps {
  workspaceId: string;
  projectId: string;
  filterId: string;
  callback: () => Promise<void>;
  children(freeNav: IFreeAccessNavigationProps, backNav: IBackNavigationProps): ReactNode;
}

export interface IFreeAccessNavigationProps {
  previousLink: string;
  nextLink: string;
  total: number;
  current: number;
  isLoading: boolean;
  goTo: (link: string) => void;
}

export interface IBackNavigationProps {
  quitLink: string;
  goTo(link: string): void;
}

export const FreeAccessNavigationContainer = observer((props: IProps): ReactElement => {
  const freeAccessStore = useInjection(IFreeAccessStore.$);
  const uiData = useInjection(IAnnotationUiStore.$);
  const historyService = useInjection(IUndoRedoHistory.$);
  const freeAccessService = useInjection(IFreeAccessService.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);

  const { workspaceId, projectId, filterId } = props;
  const { isLoading, withLoaderAsync } = useLoader();
  const history = useHistory();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentLink, setCurrentLink] = useState<string | undefined>(undefined);
  const [confirmedNavigation, setConfirmedNavigation] = useState<boolean>(false);

  const handleNavigation = (link: string): void => {
    if (freeAccessStore.freeAccessMode !== FreeAccessMode.VIEW && freeAccessStore.freeAccessMode !== FreeAccessMode.REVIEW && historyService.canUndo) {
      setShowModal(true);
      setCurrentLink(link);
    } else {
      annotationsStore.setHiddenSegmentations([]);
      history.push(link);
      setShowModal(false);
    }
  };

  const handleQuitCancel = (): void => {
    setShowModal(false);
    setCurrentLink(undefined);
  };

  const handleQuitConfirm = async (): Promise<void> => {
    historyService.clearHistory();
    history.push(currentLink || '');
    setShowModal(false);
    setConfirmedNavigation(true);
    setCurrentLink(undefined);
    return Promise.resolve();
  };

  const handleToggleDraft = (): void => {
    setShowModal(false);
    setCurrentLink(undefined);
  };

  const handleCancelDraft = (): void => {
    historyService.clearHistory();
    history.push(currentLink || '');
    setShowModal(false);
    setConfirmedNavigation(true);
    setCurrentLink(undefined);
  };

  const handleSaveDraft = async (): Promise<void> => {
    setShowModal(false);
    await withLoaderAsync(async (): Promise<void> => {
      if (getEditorMode() === EditorMode.FREEACCESS_EDIT) {
        await freeAccessService.editAnnotationAsync();
      } else {
        await freeAccessService.saveAnnotationDraftAsync();
      }
      historyService.clearHistory();
    });
    history.push(currentLink || '');
    setShowModal(false);
    setConfirmedNavigation(true);
    setCurrentLink(undefined);
  };

  const handleBlockedNavigation = (): boolean => {
    if (!confirmedNavigation && historyService.canUndo) {
      setShowModal(true);
      return false;
    }
    return true;
  };

  const getEditorMode = (): EditorMode =>
    freeAccessStore.freeAccessMode === FreeAccessMode.ANNOTATIONRESET ? EditorMode.FREEACCESS_EDIT : EditorMode.FREEACCESS_ANNOTATION;

  const { previousId, nextId } = freeAccessStore;
  const freeNav = {
    nextLink: Home.FreeAccess.View.withParams({ workspaceId, projectId, filterId, imageId: nextId }),
    previousLink: Home.FreeAccess.View.withParams({ workspaceId, projectId, filterId, imageId: previousId }),
    isLoading: isLoading || (uiData.isImageLoading && !freeAccessStore.showImageDeletedMessage),
    current: freeAccessStore.index,
    total: freeAccessStore.total,
    goTo: handleNavigation,
  };
  const backNav = {
    quitLink: Home.Projects.Details.Images.withParams({ workspaceId, projectId }),
    goTo: handleNavigation,
  };

  const showDraftModal = freeAccessStore.annotateStage === AnnotateStage.NOTANNOTATED || freeAccessStore.annotateStage === AnnotateStage.DRAFT;

  return (
    <>
      {props.children(freeNav, backNav)}
      <Prompt
        when={true}
        message={handleBlockedNavigation}
      />
      <SaveDraftAnnotationsModal
        isOpen={showModal && showDraftModal}
        onCancel={handleCancelDraft}
        onConfirm={handleSaveDraft}
        onToggle={handleToggleDraft}
        isLoading={isLoading}
      />
      <QuitAnnotationsModal
        isOpen={showModal && !showDraftModal}
        onCancel={handleQuitCancel}
        onConfirm={handleQuitConfirm}
      />
    </>
  );
});
